import * as React from 'react'
import {ArrowContainer, Popover as TinyPopover} from 'react-tiny-popover'

import {USE_DISMISS_IGNORED_IDENTIFIER} from 'airship/hooks/use-dismiss'

import classNames = require('classnames')

/** This popover component leverages react-tiny-popover.
 *  https://www.npmjs.com/package/react-tiny-popover
 */
export interface Props {
  /** Whether the popover is visible or not */
  isOpen: boolean
  /** Method to be called when a user clicks outside the popover or presses the escape key */
  onClose: (
    reason: CloseReason,
    event?: React.MouseEvent<HTMLElement, MouseEvent>
  ) => void
  /** prevents automatic readjustment of content position that keeps your popover content within its parent's bounds */
  reposition?: boolean
  /** Popover position in relation to its target element */
  position?: PopoverPosition
  /** Alignment of popover arrow in relation to the target element */
  align?: PopoverAlignment
  /** The element by which the popover is positioned */
  targetElement?: JSX.Element
  /** Target Element displayclasses */
  targetDisplayClasses?: string
  /** Popover Menu displayclasses */
  menuDisplayClass?: string
  /** Render without an arrow */
  noArrow?: boolean
  /** Optional classes to add to the popover body */
  displayClasses?: string
  /** Optional arrow color */
  arrowColor?: string
  /** Optional arrow border color */
  arrowBorderColor?: string
  /** Optional spacing between target and popover */
  spacing?: number
}

export const popoverPosition = ['top', 'bottom', 'left', 'right'] as const
export const popoverAlignment = ['start', 'center', 'end'] as const
export type PopoverPosition = (typeof popoverPosition)[number]
export type PopoverAlignment = (typeof popoverAlignment)[number]
export type CloseReason = 'clickOutside' | 'escapePress'

function getArrowStyle(
  position: PopoverPosition,
  arrowSize: number,
  arrowColor: string
): React.CSSProperties {
  switch (position) {
    case 'left':
      return {
        right: 2,
        borderLeft: `${arrowSize}px solid ${arrowColor}`,
        zIndex: 1,
      }
    case 'right':
      return {
        left: 2,
        borderRight: `${arrowSize}px solid ${arrowColor}`,
        zIndex: 1,
      }
    case 'bottom':
      return {
        top: 2,
        borderBottom: `${arrowSize}px solid ${arrowColor}`,
        zIndex: 1,
      }
    case 'top':
      return {
        bottom: 2,
        borderTop: `${arrowSize}px solid ${arrowColor}`,
        zIndex: 1,
      }
  }
}

const Popover: React.FC<React.PropsWithChildren<Props>> = ({
  targetElement,
  position = 'bottom',
  reposition,
  align = 'center',
  isOpen,
  onClose,
  displayClasses,
  children,
  noArrow,
  arrowColor = 'white',
  arrowBorderColor = '#A3A4A8', // var(--grey-60)
  targetDisplayClasses,
  menuDisplayClass,
  spacing,
}) => {
  const arrowSize = 10

  React.useEffect(() => {
    window.addEventListener('keyup', keyHandler)

    return () => {
      window.removeEventListener('keyup', keyHandler)
    }

    function keyHandler(ev: KeyboardEvent): void {
      if (ev.key === 'Escape') {
        onClose('escapePress')
      }
    }
  }, [onClose])
  const inner = React.useMemo(
    () => (
      <div
        className={classNames(
          USE_DISMISS_IGNORED_IDENTIFIER,
          displayClasses,
          'popover-body'
        )}
      >
        {children}
      </div>
    ),
    [children, displayClasses]
  )

  return (
    <TinyPopover
      reposition={reposition}
      isOpen={isOpen}
      positions={[position]}
      align={align}
      padding={noArrow ? 0 : spacing ? spacing : arrowSize}
      containerClassName={classNames('popover-menu', menuDisplayClass)}
      onClickOutside={() => onClose('clickOutside')}
      content={({position: popoverPosition, childRect, popoverRect}) =>
        noArrow ? (
          inner
        ) : (
          <ArrowContainer
            position={popoverPosition}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowColor={arrowBorderColor}
            arrowSize={arrowSize}
            arrowClassName="popover-arrow"
          >
            <ArrowContainer
              position={popoverPosition}
              childRect={childRect}
              popoverRect={popoverRect}
              arrowColor={arrowColor}
              arrowSize={arrowSize}
              arrowStyle={getArrowStyle(position, arrowSize, arrowColor)}
              style={{
                paddingLeft: 0,
                paddingTop: 0,
                paddingBottom: 0,
                paddingRight: 0,
              }}
            >
              {inner}
            </ArrowContainer>
          </ArrowContainer>
        )
      }
    >
      <span className={classNames('popover-target', targetDisplayClasses)}>
        {targetElement}
      </span>
    </TinyPopover>
  )
}

export default Popover
